/**
 * The player card (docs/design/keyracing.md D1b): what a town player looks like
 * on paper — the rating plate that lives in the right-click menu, and the modal
 * its Examine verb opens.
 *
 * **This file decides wording, never facts.** Which ladder represents someone,
 * what their Elo is, what they have raced: all of that is resolved server-side
 * (`Accounts.playerCard`) and arrives on `ServerOp.PlayerCard`, because two
 * clients disagreeing about which ladder a player is read by is the bug a
 * server-side rule prevents. What lives here is the part only a client can know:
 * which words fit in a menu, and what a provisional number should look like next
 * to a settled one.
 *
 * The one rule worth defending: **there is no "unrated" state to display.** An
 * account with no rows IS the provisional start (μ = 0, φ = 350 → 1500), so the
 * plate always draws a number and the qualifier is the race count — never a word
 * that implies the ladder measured someone and found them wanting. The word
 * *unrated* belongs to a race term (`RaceWire.rated`), which the challenge's own
 * terms pane shows on the same screen.
 */
import {
  EQUIP_SLOTS,
  ITEMS,
  partyLabel,
  SLOT_LABELS,
  type HeroAppearance,
  type ItemDef,
  type PlayerCardWire,
  type RatingLadderWire,
} from "@pd/shared";
import { createGameModal, type GameModal } from "./modal.js";
import { formatRunTime } from "./results.js";
import { drawHero, heroOutfitBounds, outfitAnchorPx, type HeroAssets } from "./hero.js";
import { packHeroLut } from "./heroLut.js";

/** The provisional start, as the rating model states it (glicko2's RD 350 →
 *  1500 on the display scale). A display constant, not a rating: the server
 *  never sends it as someone's number, the plate draws it when nobody has one. */
export const PROVISIONAL_ELO = 1500;

/** The five ladders, prestige order — the same list the hiscore reads. */
export const LADDER_CATEGORIES = [1, 2, 3, 4, 5] as const;

/**
 * What the plate says. Kept as plain strings so the menu, the modal and a test
 * all read one rule for wording.
 *
 * `ladder` is empty when no ladder is above the provisional start: the display
 * rule breaks Elo ties toward the LARGER group size, and a never-raced account
 * ties on all five, so naming one would claim a ladder they have never played.
 */
export interface PlateCopy {
  /** Drives the plate's look: a settled number, a provisional one, or neither. */
  state: "loading" | "settled" | "provisional";
  /** The number, already a string. `···` while the card is in flight. */
  elo: string;
  /** The ladder word (`Trio`, `5 man`), or "" when there is no ladder to name. */
  ladder: string;
  /** The quiet line under the number: how much rating stands behind it. */
  note: string;
}

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`;

export function plateCopy(card: PlayerCardWire | undefined): PlateCopy {
  if (card === undefined) {
    // In flight. The plate holds its own height so the menu does not jump under
    // the cursor when the answer lands (traps/dom-rebuild.md — a menu that
    // reflows mid-hover loses the row the pointer was on).
    return { state: "loading", elo: "···", ladder: "", note: "rating…" };
  }
  const shown = card.shown ?? null;
  if (shown === null) {
    return { state: "provisional", elo: String(PROVISIONAL_ELO), ladder: "", note: "provisional · no rated races" };
  }
  const ladder = partyLabel(shown.category);
  if (shown.races === 0) {
    // Their own override, pointing at a ladder they have never raced. A legal
    // choice, and the honest answer is the provisional start ON THAT LADDER.
    return { state: "provisional", elo: String(shown.elo), ladder, note: "provisional · unraced on this ladder" };
  }
  const races = plural(shown.races, "rated race");
  return {
    state: shown.provisional ? "provisional" : "settled",
    elo: String(shown.elo),
    ladder,
    note: shown.provisional ? `provisional · ${races}` : races,
  };
}

/**
 * The plate: a readout, not a verb. It is appended to the context menu under the
 * title and is never hoverable or clickable — a menu that offers "1523" as a
 * thing you can DO is a menu that lies about what it is.
 */
export function buildRatingPlate(card: PlayerCardWire | undefined): HTMLElement {
  const copy = plateCopy(card);
  const plate = document.createElement("div");
  plate.className = `rating-plate state-${copy.state}`;
  const main = document.createElement("div");
  main.className = "rating-plate-main";
  const elo = document.createElement("span");
  elo.className = "rating-elo";
  elo.textContent = copy.elo;
  main.appendChild(elo);
  if (copy.ladder !== "") {
    const ladder = document.createElement("span");
    ladder.className = "rating-ladder";
    ladder.textContent = copy.ladder;
    main.appendChild(ladder);
  }
  const note = document.createElement("div");
  note.className = "rating-note";
  note.textContent = copy.note;
  plate.append(main, note);
  return plate;
}

/* ---- the modal ---------------------------------------------------------- */

export interface CardModalDeps {
  /** The hero part assets, when the atlas loaded. Null = the card still works
   *  and says so: an art failure may never sit on the path to someone's rating
   *  (traps/decode-race.md — caches test liveness, decoration never does). */
  assets: HeroAssets | null;
  /** The gear catalog to name items with: the server's live store when the hub
   *  has one, the committed catalog otherwise. */
  items?: readonly ItemDef[];
}

/** One ladder row: the word, the number, the RD, the race count. A ladder with
 *  no row reads as a dash rather than 1500 — in the table, unlike the plate, the
 *  absence IS the information (which ladders you have raced is the question). */
function ladderRow(ladder: RatingLadderWire | undefined, category: number, shown: boolean): HTMLElement {
  const row = document.createElement("div");
  row.className = "pc-ladder" + (shown ? " shown" : "") + (ladder ? "" : " empty");
  const name = document.createElement("span");
  name.className = "pc-ladder-name";
  name.textContent = partyLabel(category);
  const elo = document.createElement("span");
  elo.className = "pc-ladder-elo";
  elo.textContent = ladder ? String(ladder.elo) : "—";
  const detail = document.createElement("span");
  detail.className = "pc-ladder-detail";
  detail.textContent = ladder ? `±${ladder.rd} · ${plural(ladder.races, "race")}` : "unraced";
  row.append(name, elo, detail);
  return row;
}

function section(title: string, rows: readonly HTMLElement[]): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "pc-section";
  const head = document.createElement("div");
  head.className = "pc-section-head";
  head.textContent = title;
  wrap.appendChild(head);
  for (const row of rows) wrap.appendChild(row);
  return wrap;
}

function itemLabel(kind: number | null, items: readonly ItemDef[]): string {
  if (kind === null) return "—";
  const def = items.find((i) => i.kind === kind);
  return def?.name ?? `#${kind}`;
}

/**
 * Open a player's card. The caller owns the returned modal's `close()`; the
 * modal itself owns Escape, backdrop dismissal and focus (modal.ts), so a card
 * opened from a menu cannot strand the town without a way out
 * (traps/stuck-state.md — the hide path is the part that gets forgotten).
 */
export function openPlayerCard(
  parent: HTMLElement,
  card: PlayerCardWire,
  deps: CardModalDeps,
): GameModal {
  const modal = createGameModal(parent, card.name, "player-card");
  const items = deps.items ?? ITEMS;

  // ---- the hero, and the rating plate again at modal scale ---------------
  const top = document.createElement("div");
  top.className = "pc-top";
  const figure = document.createElement("div");
  figure.className = "pc-figure";
  if (deps.assets && card.appearance) figure.appendChild(drawFigure(deps.assets, card.appearance));
  else {
    const quiet = document.createElement("div");
    quiet.className = "pc-quiet";
    quiet.textContent = card.appearance ? "art unavailable" : "no saved look";
    figure.appendChild(quiet);
  }
  const who = document.createElement("div");
  who.className = "pc-who";
  const name = document.createElement("div");
  name.className = "pc-name";
  name.textContent = card.name;
  const cls = document.createElement("div");
  cls.className = "pc-class";
  cls.textContent = card.cls ? card.cls : "no class";
  who.append(name, cls);
  top.append(figure, who);
  top.appendChild(buildRatingPlate(card));
  modal.body.appendChild(top);

  // ---- the ladders -------------------------------------------------------
  const shownCategory = card.shown?.category ?? -1;
  const ladders = new Map((card.ladders ?? []).map((l) => [l.category, l]));
  modal.body.appendChild(section(
    card.override ? "Racing — their ladder" : "Racing — best ladder",
    LADDER_CATEGORIES.map((c) => ladderRow(ladders.get(c), c, c === shownCategory)),
  ));

  // ---- the build they go in with ----------------------------------------
  const slots = card.equipment
    ? EQUIP_SLOTS.map((slot) => {
      const row = document.createElement("div");
      row.className = "pc-slot";
      const label = document.createElement("span");
      label.className = "pc-slot-label";
      label.textContent = SLOT_LABELS[slot];
      const value = document.createElement("span");
      value.className = "pc-slot-value";
      value.textContent = itemLabel(card.equipment?.[slot] ?? null, items);
      row.append(label, value);
      return row;
    })
    : [quietRow("no saved build")];
  modal.body.appendChild(section("Build", slots));

  // ---- the fastest floors ------------------------------------------------
  const bests = (card.bests ?? []).map((best) => {
    const row = document.createElement("div");
    row.className = "pc-best";
    const floor = document.createElement("span");
    floor.className = "pc-best-floor";
    floor.textContent = best.floorName;
    const bucket = document.createElement("span");
    bucket.className = "pc-best-bucket";
    bucket.textContent = `${best.size} · ${partyLabel(best.partySize).toLowerCase()}`;
    const time = document.createElement("span");
    time.className = "pc-best-time";
    time.textContent = formatRunTime(best.ticks);
    row.append(floor, bucket, time);
    return row;
  });
  modal.body.appendChild(section("Fastest floors", bests.length > 0 ? bests : [quietRow("no ranked clears yet")]));

  return modal;
}

function quietRow(text: string): HTMLElement {
  const row = document.createElement("div");
  row.className = "pc-quiet-row";
  row.textContent = text;
  return row;
}

/** The hero, facing the reader, walking in place. Drawn through the game's own
 *  `drawHero` + LUT — the same composite the town and the creator use, so a card
 *  can never show a look the game itself would not render. */
function drawFigure(assets: HeroAssets, appearance: HeroAppearance): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.className = "pc-hero";
  const hero = assets.hero;
  const outfit = (appearance.outfit ?? {}) as never;
  const scale = 2;
  if (hero) {
    const bounds = heroOutfitBounds(hero, outfit);
    const [dw, dh] = bounds.dims;
    cv.width = Math.round(dw * scale);
    cv.height = Math.round(dh * scale);
  }
  const ctx = cv.getContext("2d");
  const lut = packHeroLut(appearance.colours ?? {});
  const tint = hero?.tints?.[0] ?? "canon";
  let raf = 0;
  const paint = (t: number): void => {
    if (!ctx || !hero) return;
    const bounds = heroOutfitBounds(hero, outfit);
    const [dw, dh] = bounds.dims;
    if (cv.width !== Math.round(dw * scale)) {
      cv.width = Math.round(dw * scale);
      cv.height = Math.round(dh * scale);
    }
    ctx.clearRect(0, 0, cv.width, cv.height);
    const fps = hero.anims.walk?.fps ?? 8;
    const frame = Math.floor(t / (1000 / fps)) % 4;
    // The anchor is the feet, in CANVAS px: the same framing the creator's preview
    // uses, so the hero stands on the card's floor line with its whole body inside
    // the canvas. Passing logical px here instead lands the sprite `anchor × scale`
    // above its box — the head drawn past the canvas's top edge and clipped away
    // (traps/coordinate-space.md).
    const [ax, ay] = outfitAnchorPx(hero, bounds, scale);
    drawHero(ctx, assets, tint, outfit, "walk", "s", frame, ax, ay, scale, lut);
  };
  const step = (t: number): void => {
    paint(t);
    raf = requestAnimationFrame(step);
  };
  paint(0);
  if (typeof requestAnimationFrame === "function") raf = requestAnimationFrame(step);
  // The card's own teardown stops the loop: a modal that is closed but still
  // animating is a leak with a heartbeat.
  const observer = new MutationObserver(() => {
    if (!cv.isConnected) {
      cancelAnimationFrame(raf);
      observer.disconnect();
    }
  });
  observer.observe(cv.parentElement ?? document.body, { childList: true, subtree: true });
  return cv;
}
